import { useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from "components/GlobalContext";
import { Card, Col, Form, Row } from "react-bootstrap";
import IProduct, { IProductCreateOrUpdate, IProductInitResult } from "models/sql/IProduct";
import ProductService from "services/ProductService";
import MarkdownEditor from "components/MarkdownEditor";
import { useBeforeUnload } from "utilities/UIUtilities";
import DetailFooter from "components/DetailFooter";
import AccordionGroup from "components/AccordionGroup";
import { getBaseDomain } from "utilities/MiscUtilities";
import FileUploadEntry from "components/FileUploadEntry";

interface IProductsDetailProps {
    baseData?: IProductInitResult;
}

const ProductsDetail = (props: IProductsDetailProps) => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const { id }: any = useParams();
    const [product, setProduct] = useState<IProduct | null>({
        name: "",
        partNumber: "",
        productId: -1,
        serialPrefix: "",
        description: "",
        equipment: undefined,
        imagePath: "",
        postRegistrationNotificationDays: 0,
        postRegistrationNotificationMessage: "",
        postRegistrationPath: "",
        cost: 0,
        listPrice: 0,
        notes: "",
        avgSellPrice: 0,
        productType: "",
        publish: false,
        qtyOnOrder: 0,
        shortDescription: "",
        infoUrl: "",
        quotable: false,
        warrantyMonths: 0,
        category: undefined,
        hcCategory: undefined,
        websitePrice: 0,
        colors: "",
        specifications: "",
        forSale: false,
        webPublished: false,
        listingIndexId: undefined,
        pdfs: undefined,
        websiteImages: undefined,
        instructionsUrl: undefined,
        shoppingUrl: undefined,
        accessoryUrl: undefined,
        shopifyProductId: undefined
    });
    const navigate = useNavigate();
    const [hasFormDataChanged, setHasFormDataChanged] = useState(false);
    const productTypes = ["Equipment", "Consumable"];
    const productCategories = ["Facility Air Decon", "Vehicle Air Decon", "Gross Decon", "Filters & Consumables", "Diagnostic Tools", "Signage"];
    const hcCategories = ["Incident", "Vehicle", "Facilities", "Team"];

    useBeforeUnload(
        hasFormDataChanged,
        "You have unsaved changes, please save them before leaving the page.");

    useEffect(
        () => {
            (async () => {
                if (id !== undefined) {
                    globalContext.setLoading(true);

                    const accessToken = await globalContext.getAccessTokenAsync();
                    const result = await ProductService.GetAsync(accessToken, id);

                    if (result.record) {
                        setProduct(result.record);
                    }
                    else {
                        setProduct(null);
                        globalContext.showErrorMessage("Unable to load Product");
                    }

                    globalContext.setLoading(false);
                }
            })();
        }, [id]);

    const deleteAsync = async () => {
        if (product && product.productId && window.confirm("Are you SURE you want to delete this?")) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await ProductService.DeleteAsync(accessToken, product.productId);
            globalContext.setLoading(false);
            navigate("/Products");
        }
    };

    const submitFormAsync = async () => {
        if (product) {
            globalContext.setLoading(true);
            const accessToken = await globalContext.getAccessTokenAsync();

            const data: IProductCreateOrUpdate = {
                name: product.name,
                partNumber: product.partNumber,
                productId: product.productId,
                serialPrefix: product.serialPrefix,
                description: product.description,
                notes: product.notes,
                imagePath: product.imagePath,
                postRegistrationNotificationDays: product.postRegistrationNotificationDays,
                postRegistrationNotificationMessage: product.postRegistrationNotificationMessage,
                postRegistrationPath: product.postRegistrationPath,
                cost: product.cost,
                listPrice: product.listPrice,
                avgSellPrice: product.avgSellPrice,
                productType: product.productType,
                publish: product.publish,
                qtyOnOrder: product.qtyOnOrder,
                infoUrl: product.infoUrl,
                shortDescription: product.shortDescription,
                quotable: product.quotable,
                warrantyMonths: product.warrantyMonths,
                category: product.category,
                hcCategory: product.hcCategory,
                websitePrice: product.websitePrice,
                colors: product.colors,
                specifications: product.specifications,
                forSale: product.forSale,
                webPublished: product.webPublished,
                listingIndexId: product.listingIndexId,
                pdfs: product.pdfs,
                websiteImages: product.websiteImages,
                instructionsUrl: product.instructionsUrl,
                shoppingUrl: product.shoppingUrl,
                accessoryUrl: product.accessoryUrl,
                shopifyProductId: product.shopifyProductId
            };

            await ProductService.CreateOrUpdateAsync(accessToken, data);
            globalContext.setLoading(false);
            setHasFormDataChanged(false);

            if (data.productId === -1) {
                navigate(-1);
            }
        }
    };

    if (product) {
        return (
            <>
                <Row>
                    <Col>
                        <Card className="mb-2 mt-2">
                            <Card.Body>
                                <Card.Title>
                                    {
                                        id !== undefined &&
                                        <>
                                            <strong>{product.name} ({product.partNumber})</strong><br />
                                            {
                                                product.webPublished &&
                                                <a
                                                    href={`https://${getBaseDomain()}/Product/${product.name}`}
                                                    target="_blank">View Site Product Page</a>
                                            }
                                            {
                                                !product.webPublished &&
                                                <span style={{ color: "red", fontWeight: "bold" }}>Product is not published to the website</span>
                                            }
                                        </>
                                    }
                                    {
                                        id === undefined &&
                                        <strong>New Product</strong>
                                    }
                                </Card.Title>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Name</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, name: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.name}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Part Number</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, partNumber: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.partNumber}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Serial Prefix</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, serialPrefix: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.serialPrefix}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Product Type</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, productType: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.productType}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    productTypes.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row className="align-items-end">
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Product Category</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, category: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.category}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    productCategories.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Health Continuum Category</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, hcCategory: e.target.value });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.hcCategory}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    hcCategories.map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Publish</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, publish: e.target.value === "true" });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.publish ? "true" : "false"}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Quotable</Form.Label>
                                            <Form.Select
                                                onChange={async (e) => {
                                                    setProduct({ ...product, quotable: e.target.value === "true" });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.quotable ? "true" : "false"}>
                                                <option key={"empty"} value={""}>-</option>
                                                {
                                                    ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                }
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Shopify Product Id</Form.Label>
                                            {
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, shopifyProductId: e.target.value });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.shopifyProductId}
                                                />
                                            }
                                        </Form.Group>
                                    </Col>
                                    <Col></Col>
                                    <Col></Col>
                                    <Col></Col>
                                </Row>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Cost, Price, Lead Time"
                                    loading={globalContextRef.current.loading}
                                >
                                    <Row className="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>List Price</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setProduct({ ...product, listPrice: parseFloat(e.target.value) });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={product.listPrice}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Website Price</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, websitePrice: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.websitePrice}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Cost</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    onChange={
                                                        (e: React.ChangeEvent<HTMLInputElement>) => {
                                                            setProduct({ ...product, cost: parseFloat(e.target.value) });
                                                            setHasFormDataChanged(true);
                                                        }
                                                    }
                                                    value={product.cost}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Average Sell Price</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setProduct({ ...product, avgSellPrice: parseFloat(e.target.value) });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={product.avgSellPrice}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </AccordionGroup>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Website Information"
                                    loading={globalContextRef.current.loading}>
                                    <>
                                        <Row>
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Short Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                // @ts-ignore
                                                                setProduct({ ...product, shortDescription: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={(product?.shortDescription || "")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Description</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={10}
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                // @ts-ignore
                                                                setProduct({ ...product, description: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={(product?.description || "")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Colors <i>(Comma seperated html color values)</i></Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                // @ts-ignore
                                                                setProduct({ ...product, colors: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={(product?.colors || "")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Specifications<i>(Comma seperated phrases like 'Weight - 5lbs')</i></Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={10}
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                // @ts-ignore
                                                                setProduct({ ...product, specifications: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={(product?.specifications || "")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row className="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>For Sale</Form.Label>
                                                    <Form.Select
                                                        onChange={async (e) => {
                                                            setProduct({ ...product, forSale: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }}
                                                        value={product.forSale ? "true" : "false"}>
                                                        <option key={"empty"} value={""}>-</option>
                                                        {
                                                            ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                        }
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Web Published</Form.Label>
                                                    <Form.Select
                                                        onChange={async (e) => {
                                                            setProduct({ ...product, webPublished: e.target.value === "true" });
                                                            setHasFormDataChanged(true);
                                                        }}
                                                        value={product.webPublished ? "true" : "false"}>
                                                        <option key={"empty"} value={""}>-</option>
                                                        {
                                                            ["true", "false"].map((s, index) => <option key={s} value={s}>{s}</option>)
                                                        }
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Listing Index Id</Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, listingIndexId: parseInt(e.target.value || "0") });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.listingIndexId || 0}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                    </>
                                </AccordionGroup>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Documents"
                                    loading={globalContextRef.current.loading}>
                                    <>
                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>
                                                        Informational Url<br />
                                                        <i>(URL for "View on Website" button on qrcode landing page)</i>
                                                    </Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, infoUrl: e.target.value });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.infoUrl}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>
                                                        Shopping Url<br />
                                                        <i>(URL for "Purchase Another Unit" button on qrcode landing page)</i>
                                                    </Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, shoppingUrl: e.target.value });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.shoppingUrl}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>
                                                        Instructional Url<br />
                                                        <i>(URL for "Installation" button on qrcode landing page)</i>
                                                    </Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, instructionsUrl: e.target.value });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.instructionsUrl}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>
                                                        Accessory Url<br />
                                                        <i>(URL for "Purchase Parts" button on qrcode landing page)</i>
                                                    </Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, accessoryUrl: e.target.value });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.accessoryUrl}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <FileUploadEntry
                                                    title={<>Attached PDFs <i>(Comma seperated values in the format 'PDF NAME|PATH TO FILE')</i></>}
                                                    defaultValue={product?.pdfs || ""}
                                                    includeTitle={true}
                                                    allowMultiple={true}
                                                    rows={4}
                                                    onChange={newVal => {
                                                        setProduct({ ...product, pdfs: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                </AccordionGroup>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Notifications"
                                    loading={globalContextRef.current.loading}>
                                    <>
                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Post Registration Notification Days</Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, postRegistrationNotificationDays: parseInt(e.target.value || "0") });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.postRegistrationNotificationDays || 0}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Warranty Months</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setProduct({ ...product, warrantyMonths: parseInt(e.target.value) });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={product.warrantyMonths}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>
                                                        Post Registration Path<br />
                                                        <i>(THIS WILL OVERRIDE THE LANDING PAGE PAGE AFTER REGISTRATION)</i>
                                                    </Form.Label>
                                                    {
                                                        <Form.Control
                                                            type="text"
                                                            onChange={
                                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                    setProduct({ ...product, postRegistrationPath: e.target.value });
                                                                    setHasFormDataChanged(true);
                                                                }
                                                            }
                                                            value={product.postRegistrationPath}
                                                        />
                                                    }
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <Form.Group className="mb-3" >
                                                    <Form.Label>Post Registration Notification Message</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={4}
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                // @ts-ignore
                                                                setProduct({ ...product, postRegistrationNotificationMessage: e.target.value });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={(product?.postRegistrationNotificationMessage || "")}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                </AccordionGroup>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Inventory Information"
                                    loading={globalContextRef.current.loading}>
                                    <Row ClassName="align-items-end">
                                        <Col>
                                            <Form.Group className="mb-3" >
                                                <Form.Label>Quantity On Order</Form.Label>
                                                {
                                                    <Form.Control
                                                        type="text"
                                                        onChange={
                                                            (e: React.ChangeEvent<HTMLInputElement>) => {
                                                                setProduct({ ...product, qtyOnOrder: parseInt(e.target.value || "0") });
                                                                setHasFormDataChanged(true);
                                                            }
                                                        }
                                                        value={product.qtyOnOrder || 0}
                                                    />
                                                }
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </AccordionGroup>

                                <AccordionGroup
                                    className="mb-3"
                                    title="Images"
                                    loading={globalContextRef.current.loading}>
                                    <>
                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <FileUploadEntry
                                                    title="Image Path"
                                                    defaultValue={product?.imagePath || ""}
                                                    onChange={newVal => {
                                                        setProduct({ ...product, imagePath: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                        <Row ClassName="align-items-end">
                                            <Col>
                                                <FileUploadEntry
                                                    title={<>Product Images <i>(Comma seperated list of image urls)</i></>}
                                                    defaultValue={product?.websiteImages || ""}
                                                    allowMultiple={true}
                                                    rows={4}
                                                    onChange={newVal => {
                                                        setProduct({ ...product, websiteImages: newVal });
                                                        setHasFormDataChanged(true);
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                </AccordionGroup>

                                {
                                    globalContext.roles.some(r => r === "Web.Admin") &&
                                    <Row>
                                        <Form.Group className="mb-3" >
                                            <Form.Label>Admin Notes</Form.Label>
                                            <MarkdownEditor
                                                globalContextRef={globalContextRef.current}
                                                noun="Note"
                                                onChange={newVal => {
                                                    setProduct({ ...product, notes: newVal });
                                                    setHasFormDataChanged(true);
                                                }}
                                                value={product.notes || ""}
                                            />
                                        </Form.Group>
                                    </Row>
                                }
                            </Card.Body>
                        </Card>
                    </Col>
                </Row >

                {
                    globalContext.roles.some(r => r === "Web.Admin") &&
                    <DetailFooter
                        hasFormDataChanged={hasFormDataChanged}
                        deleteAsync={deleteAsync}
                        submitFormAsync={submitFormAsync}
                    />
                }
            </>
        );
    }
    else {
        return (
            <>
                <h1>There was an issue retreiving the Product, please try again.</h1>
            </>
        );
    }
}

export default ProductsDetail;
